/**
 * Results API helpers
 * ===================
 * Thin wrappers around the document backend result endpoints.
 */

import { getToken } from '../../utils/auth';
import buildBlobUrl from '../../utils/buildBlobUrl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const authHeaders = (schoolId) => ({
  Authorization: `Bearer ${getToken(schoolId)}`,
});

const handle = async (res) => {
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Request failed');
  }
  return data.data;
};

/** List all exams with their result counts. */
export const fetchExams = async (schoolId) => {
  const res = await fetch(`${API_URL}/results/exams`, { headers: authHeaders(schoolId) });
  return handle(res);
};

/** Compact result table for a single exam (optional class/section filter). */
export const fetchExamResults = async (schoolId, examId, { className, section } = {}) => {
  const params = new URLSearchParams();
  if (className) params.set('className', className);
  if (section) params.set('section', section);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/results/exam/${encodeURIComponent(examId)}${qs}`, {
    headers: authHeaders(schoolId),
  });
  return handle(res);
};

/** Grade bands configured for the school. Returns { grades, isDefault }. */
export const fetchGrades = async (schoolId) => {
  const res = await fetch(`${API_URL}/results/grades`, { headers: authHeaders(schoolId) });
  return handle(res);
};

/** Update one student's marks. `marks` = { [subject]: { theory, practical } }. */
export const updateStudentMarks = async (schoolId, examId, studentId, marks) => {
  const res = await fetch(`${API_URL}/results/exam/${encodeURIComponent(examId)}/marks`, {
    method: 'PUT',
    headers: { ...authHeaders(schoolId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ studentId, marks }),
  });
  return handle(res);
};

/** Save grade bands. `grades` = [{ grade, min, max }]. */
export const saveGrades = async (schoolId, grades) => {
  const res = await fetch(`${API_URL}/results/grades`, {
    method: 'POST',
    headers: { ...authHeaders(schoolId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ grades }),
  });
  return handle(res);
};

/** Distinct class(+section) labels for pickers. Returns { options: ['1-A', ...] }. */
export const fetchClassOptions = async (schoolId) => {
  const res = await fetch(`${API_URL}/results/class-options`, { headers: authHeaders(schoolId) });
  return handle(res);
};

/** Custom class groups configured for reports. Returns { groups }. */
export const fetchClassGroups = async (schoolId) => {
  const res = await fetch(`${API_URL}/results/class-groups`, { headers: authHeaders(schoolId) });
  return handle(res);
};

/** Save class groups. `groups` = [{ name, classes: ['Nur', '1-A'] }]. */
export const saveClassGroups = async (schoolId, groups) => {
  const res = await fetch(`${API_URL}/results/class-groups`, {
    method: 'POST',
    headers: { ...authHeaders(schoolId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ groups }),
  });
  return handle(res);
};

/** Co-scholastic areas printed on report cards. Returns { areas }. */
export const fetchCoScholastic = async (schoolId) => {
  const res = await fetch(`${API_URL}/results/co-scholastic`, { headers: authHeaders(schoolId) });
  return handle(res);
};

/** Save co-scholastic areas. `areas` = ['Art Education', ...]. */
export const saveCoScholastic = async (schoolId, areas) => {
  const res = await fetch(`${API_URL}/results/co-scholastic`, {
    method: 'POST',
    headers: { ...authHeaders(schoolId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ areas }),
  });
  return handle(res);
};

/** Report-card signatures. Returns { signatures: [{ label, blob, url }], isDefault }. */
export const fetchSignatures = async (schoolId) => {
  const res = await fetch(`${API_URL}/results/signatures`, { headers: authHeaders(schoolId) });
  const data = await handle(res);
  return {
    ...data,
    signatures: (data.signatures || []).map((s) => ({ ...s, url: s.blob ? buildBlobUrl(s.blob) : '' })),
  };
};

/** Save signatures (max 4). Entries may carry imageBase64/imageName/contentType for new uploads. */
export const saveSignatures = async (schoolId, signatures) => {
  const res = await fetch(`${API_URL}/results/signatures`, {
    method: 'POST',
    headers: { ...authHeaders(schoolId), 'Content-Type': 'application/json' },
    body: JSON.stringify({ signatures }),
  });
  return handle(res);
};

/** School info (name, address, logo) for document headers. Logo resolved to a full URL. */
export const fetchSchoolInfo = async (schoolId) => {
  const res = await fetch(`${API_URL}/school/info`, { headers: authHeaders(schoolId) });
  const info = await handle(res);
  if (info?.schoolLogo) info.schoolLogo = buildBlobUrl(info.schoolLogo);
  return info;
};

/** Resolve a percentage to its grade label using the configured bands. */
export const gradeForPercentage = (percentage, grades = []) => {
  const pct = Number(percentage) || 0;
  const band = grades.find((g) => pct >= Number(g.min) && pct <= Number(g.max));
  return band ? band.grade : '—';
};
